import { Shop } from "@/types/v2";
import { firestoreToDateTime } from "@/lib/utils";

export type ShopSortKey = "name" | "createdAt";
export type ShopSortOrder = "asc" | "desc";

export interface ShopListOptions {
  search: string;
  sortBy: ShopSortKey;
  sortOrder: ShopSortOrder;
}

/**
 * When the shop was created. New shops carry both `createdAt` (a Firestore
 * Timestamp stamped on write) and `createdOn` (an ISO string from
 * registerShop); shops that predate `createdAt` only have `createdOn`, so it
 * is the fallback.
 * @param shop - The shop to read the created date from.
 * @returns The created date, or undefined when the shop carries neither field.
 */
export const shopCreatedDate = (shop: Shop): Date | undefined => {
  const { createdAt } = shop as Shop & { createdAt?: unknown };
  return (
    firestoreToDateTime(
      createdAt as Parameters<typeof firestoreToDateTime>[0]
    ) ?? firestoreToDateTime(shop.createdOn)
  );
};

/**
 * Applies the audit page's search and sort to the full shop list. Search is a
 * case-insensitive substring match on the shop name. Shops with no created
 * date sort last in both directions so they never crowd the top of the list.
 * @param shops - Every shop; not mutated.
 * @param options - Search text and sort key/direction.
 * @returns A new, filtered and sorted array.
 */
export const filterAndSortShops = (
  shops: Shop[],
  { search, sortBy, sortOrder }: ShopListOptions
): Shop[] => {
  const needle = search.trim().toLowerCase();
  const matching = needle
    ? shops.filter((shop) => (shop.name ?? "").toLowerCase().includes(needle))
    : [...shops];

  const direction = sortOrder === "asc" ? 1 : -1;

  if (sortBy === "name") {
    return matching.sort(
      (a, b) =>
        direction *
        (a.name ?? "").localeCompare(b.name ?? "", undefined, {
          sensitivity: "base",
        })
    );
  }

  return matching.sort((a, b) => {
    const aTime = shopCreatedDate(a)?.getTime();
    const bTime = shopCreatedDate(b)?.getTime();
    if (aTime === undefined && bTime === undefined) return 0;
    if (aTime === undefined) return 1;
    if (bTime === undefined) return -1;
    return direction * (aTime - bTime);
  });
};
